import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  inject
} from "@angular/core";
import {
  EventModel,
  PresetManager,
  Scheduler,
  SchedulerConfig
} from "@bryntum/scheduler-thin";
import { ResourceFactMappingPattern } from "@app/models/rest/resource-fact-mapping-pattern";
import { ResourceFactMappingPatternEntry } from "@app/models/rest/resource-fact-mapping-pattern-entry";
import { CustomResourceModel } from "@app/models/bryntum-ext/custom-resource-model";
import { MatDialog } from "@angular/material/dialog";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import moment from "moment";
import {
  PagedGetOption,
  ResourceCollection
} from "@lagoshny/ngx-hateoas-client";
import { PatternEntryDialogComponent } from "../pattern-entry-dialog/pattern-entry-dialog.component";
import { MappingPatternStore } from "@app/services/store/mapping-pattern-store";
import { MappingPatternEntryStore } from "@app/services/store/mapping-pattern-entry-store";
import { firstValueFrom } from "rxjs";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

@Component({
  selector: "app-pattern-entry-scheduler",
  templateUrl: "./pattern-entry-scheduler.component.html",
  styleUrls: ["./pattern-entry-scheduler.component.scss"],
  imports: [MatButton, MatIcon]
})
export class PatternEntrySchedulerComponent
  implements OnInit, OnChanges, AfterViewInit
{
  protected element = inject(ElementRef);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  dialog = inject(MatDialog);
  private mappingPatternStore = inject(MappingPatternStore);
  private mappingPatternEntryStore = inject(MappingPatternEntryStore);

  @Input()
  pattern: ResourceFactMappingPattern;

  @Output()
  patternChanged = new EventEmitter<void>();

  @ViewChild("schedulerContainer", { static: false })
  schedulerContainer: ElementRef;

  protected scheduler: Scheduler;
  private init = false;
  private schedulerConfig: Partial<SchedulerConfig>;
  private isSaving = false;

  addPatternEntry() {
    if (!this.pattern) return;

    // Open dialog to add a new entry
    const dialogRef = this.dialog.open(PatternEntryDialogComponent, {
      width: "400px",
      data: {
        pattern: this.pattern
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.action === "save") {
        this.saveEntry(result.entry);
      }
    });
  }

  editPatternEntry(entry: ResourceFactMappingPatternEntry) {
    if (!this.pattern) return;

    // Open dialog to edit entry
    const dialogRef = this.dialog.open(PatternEntryDialogComponent, {
      width: "400px",
      data: {
        pattern: this.pattern,
        entry: entry
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.action === "save") {
          this.saveEntry(result.entry);
        } else if (result.action === "delete") {
          this.deleteEntry(result.entry);
        }
      }
    });
  }

  saveEntry(entry: ResourceFactMappingPatternEntry) {
    if (this.isSaving) return;
    this.isSaving = true;

    console.log("Saving pattern entry", entry);

    // Determine if this is a new entry or an update
    const saveObservable = entry.id
      ? this.mappingPatternEntryStore.updateEntry(entry)
      : this.mappingPatternEntryStore.createEntryForPattern(
          this.pattern,
          entry
        );

    firstValueFrom(saveObservable)
      .then((savedEntry) => {
        console.log("Pattern entry saved successfully", savedEntry);

        // Reload entries directly using advanced search instead of reloading the entire pattern
        this.loadData();
        this.patternChanged.emit();
        this.isSaving = false;
      })
      .catch((error) => {
        this.serviceErrorHandler.handleError(error);
        this.isSaving = false;
      });
  }

  deleteEntry(entry: ResourceFactMappingPatternEntry) {
    if (this.isSaving) return;
    if (!entry.id) return; // Can't delete unsaved entries

    this.isSaving = true;
    console.log("Deleting pattern entry", entry);

    // Use the entry store to delete the entry directly
    firstValueFrom(this.mappingPatternEntryStore.deleteEntry(entry))
      .then(() => {
        console.log("Pattern entry deleted successfully");

        // Reload entries directly using advanced search instead of reloading the entire pattern
        this.loadData();
        this.patternChanged.emit();
        this.isSaving = false;
      })
      .catch((error) => {
        this.serviceErrorHandler.handleError(error);
        this.isSaving = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.init && changes.pattern && this.scheduler) {
      this.loadData();
    }
  }

  loadData() {
    if (!this.pattern || !this.scheduler) return;

    console.log("Loading pattern data into scheduler", this.pattern);

    this.scheduler.eventStore.removeAll();
    this.scheduler.resourceStore.removeAll();

    // Add a single resource row for the pattern
    let patternResource = new CustomResourceModel({
      id: this.pattern.id,
      name: this.pattern.description
    });

    patternResource.set("entity", this.pattern);
    this.scheduler.resourceStore.add(patternResource);

    // Query pattern entries directly using advanced search
    const searchParams: PagedGetOption = {
      pageParams: {
        size: 1000
      },
      sort: {
        offsetDay: "ASC",
        startOffsetHour: "ASC",
        startOffsetMinute: "ASC"
      }
    };

    // Create search criteria to find entries for this pattern
    const searchCriteria = [
      {
        key: "mappingPattern.id",
        operation: "Equals", // Using proper enum value from the error message
        value: this.pattern.id
      }
    ];

    console.log("Querying pattern entries with criteria:", searchCriteria);

    this.mappingPatternEntryStore
      .advancedSearch(searchParams, searchCriteria)
      .then((response) => {
        console.log("Retrieved pattern entries:", response.resources);

        // Load pattern entries as events
        if (response.resources && response.resources.length > 0) {
          response.resources.forEach((entry) => {
            this.addEntryToScheduler(entry);
          });
        }
      })
      .catch((error) => {
        console.error("Error loading pattern entries:", error);
        this.serviceErrorHandler.handleError(error);

        // Fallback to pattern.patternEntries if query fails
        if (
          this.pattern.patternEntries &&
          this.pattern.patternEntries.length > 0
        ) {
          this.pattern.patternEntries.forEach((entry) => {
            this.addEntryToScheduler(entry);
          });
        }
      });
  }

  addEntryToScheduler(entry: ResourceFactMappingPatternEntry) {
    if (!this.scheduler) {
      console.error(
        "Cannot add entry to scheduler - scheduler not initialized"
      );
      return;
    }

    let entryModel = new EventModel();
    entryModel.set("id", entry.id);
    entryModel.set(
      "startDate",
      moment("2020-01-01")
        .add(entry.offsetDay, "day")
        .add(entry.startOffsetHour, "hour")
        .add(entry.startOffsetMinute, "minute")
        .parseZone()
        .toDate()
    );
    entryModel.set(
      "endDate",
      moment(entryModel.startDate)
        .add(entry.duration, "minute")
        .parseZone()
        .toDate()
    );
    entryModel.set("resourceId", this.pattern.id);
    entryModel.set("entity", entry);

    this.scheduler.eventStore.add(entryModel);
  }

  ngOnInit() {
    if (!this.pattern) {
      return;
    }
    if (this.init) {
      return;
    } else {
      this.init = true;
    }

    PresetManager.registerPreset("patternGrid", {
      displayDateFormat: "HH:mm",
      shiftIncrement: 1,
      shiftUnit: "day",
      tickWidth: 150,
      timeResolution: {
        unit: "day",
        increment: 1
      },
      headers: [
        {
          unit: "day",
          increment: 1,
          dateFormat: "hh:mm A",
          // The bundled typings say this renderer receives an object, but at
          // runtime Bryntum passes the cell start Date as the first positional
          // arg (an object's `startDate` came back undefined and rendered
          // "Day <today>" on every column — same as #1157/#1413). Accept `any`
          // and read whichever shape we get. Round the fractional day diff so
          // the count survives a DST change.
          renderer: function (cell: any) {
            const cellStart = cell?.startDate ?? cell;
            return `Day ${
              Math.round(
                moment(cellStart).diff(moment("2020-01-01"), "days", true)
              ) + 1
            }`;
          }
        }
      ]
    });

    // Prepare scheduler config but don't initialize yet - wait for view init
    this.prepareSchedulerConfig();
  }

  prepareSchedulerConfig() {
    // @ts-ignore
    this.schedulerConfig = {
      barMargin: 10,
      rowHeight: 100,
      columnLines: true,
      enableEventAnimations: false,
      allowOverlap: false,
      startDate: "2020-01-01",
      endDate: moment("2020-01-01")
        .add(this.pattern.length, "days")
        .parseZone()
        .toDate(),
      // Don't set appendTo yet, wait for AfterViewInit
      multiEventSelect: false,
      snapRelativeToEventStartDate: false,
      snap: false,
      scrollable: true,
      forceFit: true,
      autoHeight: true,
      fillTicks: true,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      viewPreset: "patternGrid",
      cls: "sch-container pattern-entry-sch-container",
      style: "height: 300px; width: 100%;",
      readOnly: true, // Make scheduler read-only to prevent drag/resize
      eventRenderer: (display: { eventRecord: EventModel; renderData }) => {
        return `${moment(display.eventRecord.startDate).format(
          "HH:mm"
        )}<br/>-<br/>${moment(display.eventRecord.endDate).format("HH:mm")}`;
      },
      listeners: {
        eventClick: ({ eventRecord, assignmentRecord, event }) => {
          console.log("Event clicked", eventRecord);
          const entry = eventRecord.get("entity");
          if (entry) {
            this.editPatternEntry(entry);
          }
          return false;
        },
        // Prevent double-click creation of ghost entries
        cellDblClick: () => {
          console.log("Cell double-click prevented");
          return false; // Prevent default behavior
        },
        scheduleClick: () => {
          return false; // Prevent default click behavior
        },
        scheduleDblClick: () => {
          console.log("Schedule double-click prevented");
          return false; // Prevent default behavior
        }
      },
      columns: [
        {
          type: "resourceInfo",
          locked: true,
          draggable: false,
          text: "Pattern",
          showEventCount: false,
          field: "name",
          showRole: false,
          showImage: false,
          editor: false
        }
      ],
      features: {
        eventDrag: false, // Disable dragging events
        eventDragCreate: false, // Disable drag create - use add button instead
        eventEdit: false,
        eventResize: false, // Disable event resizing
        eventMenu: {
          items: {
            editEvent: {
              text: "Edit Time Block",
              icon: "b-fa b-fa-edit",
              weight: 100,
              onItem: ({ eventRecord }) => {
                const entry = eventRecord.get("entity");
                if (entry) {
                  this.editPatternEntry(entry);
                }
              }
            },
            deleteEvent: {
              text: "Delete Time Block",
              icon: "b-fa b-fa-trash",
              weight: 200,
              onItem: ({ eventRecord }) => {
                const entry = eventRecord.get("entity");
                if (entry) {
                  this.deleteEntry(entry);
                }
              }
            },
            unassignEvent: false
          }
        },
        rowCopyPaste: false,
        eventCopyPaste: false,
        columnResize: false,
        regionResize: false,
        eventFilter: false,
        eventTooltip: {
          template: (data) => {
            const { eventRecord } = data;
            const startTime = moment(eventRecord.startDate).format("HH:mm");
            const endTime = moment(eventRecord.endDate).format("HH:mm");
            const durationInHours = (
              eventRecord.durationMS /
              (1000 * 60 * 60)
            ).toFixed(1);

            return `
              <div class="event-tooltip">
                <div class="tooltip-title">Time Block</div>
                <div>Start: ${startTime}</div>
                <div>End: ${endTime}</div>
                <div>Duration: ${durationInHours} hours</div>
                <div class="tooltip-footer">Click to edit details</div>
              </div>
            `;
          }
        },
        scheduleTooltip: false,
        timeRanges: { showHeaderElements: true, showCurrentTimeLine: false },
        resourceTimeRanges: true,
        cellEdit: false,
        filter: false,
        timeAxisHeaderMenu: {}
      }
    };
  }

  ngAfterViewInit() {
    // Now we can safely use the schedulerContainer reference
    setTimeout(() => {
      if (!this.schedulerContainer) {
        console.error("Scheduler container reference not available");
        return;
      }

      console.log("Initializing scheduler", this.schedulerContainer);

      this.schedulerConfig.appendTo = this.schedulerContainer.nativeElement;
      this.scheduler = new Scheduler(this.schedulerConfig);

      // Now that the scheduler is initialized, load the data
      this.loadData();
    }, 0);
  }
}
